import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ShieldCheck,
  Trash2,
  UserCog,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { GLASS_CARD } from '@/components/ui/glass';
import Footer from '@/components/pages/shared/Footer';

const DeleteProfile = () => {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col min-h-screen bg-white dark:bg-black text-gray-800 dark:text-gray-100">
      {/* ── HERO ────────────────────────────────────────────────────────────
          Mismo patrón que FAQ.tsx: sin header ni logo, botón "Volver"
          flotante, orbs decorativos y onda SVG de cierre. */}
      <section className="relative overflow-hidden text-white w-full">
        {/* Fondo modo claro */}
        <div
          className="absolute inset-0 dark:hidden"
          style={{
            background: `
              radial-gradient(ellipse at 15% 60%, rgba(40,142,127,0.22) 0%, transparent 50%),
              radial-gradient(ellipse at 85% 20%, rgba(16,50,84,0.5) 0%, transparent 55%),
              linear-gradient(170deg, #103254 0%, #103254 25%, #1a4a6e 45%, #3d7fa0 68%, #a8cfe0 84%, #e8f4f8 95%, #ffffff 100%)
            `,
          }}
        />
        {/* Fondo modo oscuro */}
        <div
          className="absolute inset-0 hidden dark:block"
          style={{
            background: `
              radial-gradient(ellipse at 15% 55%, rgba(40,142,127,0.35) 0%, transparent 55%),
              radial-gradient(ellipse at 85% 15%, rgba(16,50,84,0.45) 0%, transparent 55%),
              linear-gradient(170deg, #050807 0%, #0a1a16 22%, #123a30 42%, #103254 60%, #0a0f14 82%, #000000 100%)
            `,
          }}
        />

        <motion.div
          className="absolute top-10 left-[5%] w-56 h-56 rounded-full pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, rgba(40,142,127,0.18) 0%, transparent 70%)',
            filter: 'blur(40px)',
          }}
          animate={{ scale: [1, 1.1, 1], opacity: [0.5, 0.9, 0.5] }}
          transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute top-4 right-[8%] w-64 h-64 rounded-full pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, rgba(255,255,255,0.07) 0%, transparent 70%)',
            filter: 'blur(50px)',
          }}
          animate={{ scale: [1, 1.08, 1], opacity: [0.4, 0.75, 0.4] }}
          transition={{
            duration: 9,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 3,
          }}
        />

        <button
          onClick={() => navigate(-1)}
          aria-label="Volver atrás"
          className="absolute top-5 left-5 z-20 flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 text-sm text-white hover:bg-white/20 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden sm:inline font-medium">Volver</span>
        </button>

        <div className="relative z-10 pt-20 pb-28 px-6 flex flex-col items-center text-center max-w-3xl mx-auto">
          <motion.div
            className="mb-5 w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center shadow-lg"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Trash2 className="w-7 h-7 text-white" />
          </motion.div>

          <motion.h1
            className="text-4xl md:text-5xl font-bold tracking-tight"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut', delay: 0.1 }}
          >
            Eliminar perfil
          </motion.h1>

          <motion.p
            className="mt-4 text-base text-white/70 max-w-xl leading-relaxed"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut', delay: 0.3 }}
          >
            Puedes solicitar la eliminación de tu perfil de Bookoll y de los
            datos asociados a él.{' '}
            <span className="text-white/50">La acción es irreversible.</span>
          </motion.p>
        </div>

        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none pointer-events-none">
          <svg
            viewBox="0 0 1440 60"
            preserveAspectRatio="none"
            className="w-full h-10 sm:h-14"
          >
            <path
              d="M0,30 C360,60 1080,0 1440,30 L1440,60 L0,60 Z"
              className="fill-white dark:fill-black"
            />
          </svg>
        </div>
      </section>

      {/* ── CONTENIDO ───────────────────────────────────────────────────── */}
      <main className="flex-1 w-full bg-white dark:bg-black py-16">
        <div className="max-w-4xl mx-auto px-6 grid gap-6 md:grid-cols-2">
          {/* Qué ocurre */}
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.4 }}
            className={`${GLASS_CARD} rounded-2xl p-7`}
          >
            <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white mb-5 bg-gradient-to-br from-[#34a894] to-[#1e7a6e]">
              <ShieldCheck className="w-5 h-5" />
            </div>

            <h2 className="text-xl font-semibold text-[#103254] dark:text-white">
              ¿Qué ocurre al eliminarlo?
            </h2>

            <ul className="mt-5 space-y-4 text-sm text-gray-600 dark:text-gray-400">
              <li className="flex gap-3">
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-[#288E7F]" />
                Tu perfil dejará de estar disponible.
              </li>
              <li className="flex gap-3">
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-[#288E7F]" />
                Se eliminarán los datos personales asociados al perfil que
                puedan ser eliminados.
              </li>
              <li className="flex gap-3">
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-[#288E7F]" />
                Algunos datos podrán conservarse cuando exista una obligación
                legal de conservación.
              </li>
              <li className="flex gap-3">
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-[#288E7F]" />
                Procesaremos tu solicitud en un plazo máximo de 30 días conforme
                al RGPD.
              </li>
            </ul>

            <div className="mt-6 flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-xs leading-relaxed text-amber-900 dark:border-amber-500/25 dark:bg-amber-500/10 dark:text-amber-200">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                No podremos restaurar el perfil ni su contenido una vez
                procesada la solicitud.
              </span>
            </div>
          </motion.section>

          {/* Solicitar eliminación */}
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.4, delay: 0.08 }}
            className={`${GLASS_CARD} rounded-2xl p-7`}
          >
            <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white mb-5 bg-gradient-to-br from-[#2170b8] to-[#103254]">
              <UserCog className="w-5 h-5" />
            </div>

            <h2 className="text-xl font-semibold text-[#103254] dark:text-white">
              Cómo eliminar tu perfil
            </h2>

            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
              La baja se hace desde tu propio perfil, con tu sesión iniciada.
            </p>

            <ol className="mt-6 space-y-4 text-sm text-gray-600 dark:text-gray-300">
              {[
                'Inicia sesión en Bookoll.',
                'Entra en Mi perfil.',
                'Pulsa «Dar de baja mi perfil» y confirma.',
              ].map((paso, i) => (
                <li key={paso} className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#103254]/10 dark:bg-blue-400/10 text-[#103254] dark:text-blue-400 text-xs font-bold flex items-center justify-center shrink-0">
                    {i + 1}
                  </span>
                  <span className="pt-0.5">{paso}</span>
                </li>
              ))}
            </ol>

            <Button
              type="button"
              onClick={() => navigate('/panel-cliente/profile')}
              className="mt-7 w-full h-11 rounded-lg text-white border-0 hover:brightness-110"
              style={{
                background: 'linear-gradient(135deg, #288E7F 0%, #1e7a6e 100%)',
              }}
            >
              Ir a mi perfil
            </Button>

            <p className="mt-3 text-center text-xs text-gray-400 dark:text-gray-500">
              Si no has iniciado sesión, te pediremos que lo hagas primero.
            </p>
          </motion.section>
        </div>

        <div className="max-w-4xl mx-auto px-6 pt-10 text-center">
          <span className="inline-block text-xs text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 rounded-full px-4 py-1.5">
            ¿Tienes dudas antes de eliminarlo?{' '}
            <a
              href="mailto:info@bookoll.com"
              className="text-[#103254] dark:text-white font-semibold hover:opacity-80"
            >
              Escríbenos
            </a>
          </span>
        </div>
      </main>

      <div className="w-full h-px bg-gray-100 dark:bg-white/10" />
      <Footer />
    </div>
  );
};

export default DeleteProfile;